/* ──────────────────────────────────────────────────────────────────────────
 * Kumbhathon ecosystem integration — capability slots and their status.
 *
 * ── READ THIS BEFORE SHOWING THIS PAGE TO ANYONE ──
 *
 * Nashik Kumbhathon was a real initiative: an innovation programme run
 * around Simhastha 2015 in Nashik, associated with MIT Media Lab and the
 * city administration, which produced a set of crowd-management and civic
 * technology prototypes.
 *
 * NO PARTNERSHIP EXISTS between this platform and any Kumbhathon
 * participant, alumnus or company. Nobody has agreed to anything. Nothing
 * listed here is connected, and no named organisation appears in this file
 * for exactly that reason — naming a company beside an "integration" panel
 * implies a relationship that does not exist, and that misrepresentation
 * would survive being screenshotted long after any caveat was scrolled past.
 *
 * So the page is built the other way round. It lists CAPABILITY SLOTS the
 * platform has genuine gaps for — derived from this codebase's own
 * assurance layer, not from a wish list — and describes the kind of
 * capability that would fill each one. A real integration replaces the
 * slot's `status` with a named provider once an agreement exists.
 *
 * That makes this an integration roadmap rather than a partner logo wall,
 * which is both honest and the more useful artefact: it tells a startup
 * exactly what interface to build against.
 * ────────────────────────────────────────────────────────────────────────── */

export const KUMBHATHON_CONTEXT = {
  what: "Nashik Kumbhathon was an innovation programme run around Simhastha 2015, associated with MIT Media Lab and the Nashik city administration, which produced civic and crowd-management technology prototypes.",
  why: "Several capability gaps this platform reports in its own assurance layer are exactly the problems that programme worked on. Rebuilding them here would duplicate effort that already exists in the local ecosystem.",
  status: "No partnership, agreement or data-sharing arrangement exists with any Kumbhathon participant. Every integration below is UNCONNECTED and describes a capability type, not a company.",
};

export const INTEGRATION_STATUS = {
  UNCONNECTED: "UNCONNECTED",
  SPEC_READY: "SPEC_READY",
  PILOT: "PILOT",
  LIVE: "LIVE",
};

/* Each slot names the platform gap it fills, which of the 22 sections that
 * gap sits in, and — the part that makes this useful — the exact interface
 * contract a provider would implement. */
export const INTEGRATION_SLOTS = [
  {
    id: "SLOT-CROWD-COUNT",
    capability: { en: "Ground-truth crowd counting", mr: "प्रत्यक्ष गर्दी मोजणी" },
    fillsGap: "Every crowd figure in this platform is modelled. No sector is observed.",
    section: "§16 · §4",
    approach:
      "Pressure-mat, turnstile or anonymised sensor counting at gates and corridor pinch points. Aggregate counts only — no identity, no device identifiers.",
    contract: {
      direction: "inbound",
      endpoint: "POST /ingest/counts",
      payload: "{ sensorId, gateId, minuteOfDay, count, confidence }",
      cadence: "1-minute aggregate",
      classification: "PUBLIC (aggregate) — must contain no per-person data",
    },
    unlocks: [
      "Detection latency becomes measurable (§22 metric 1)",
      "Forecast accuracy can be scored against reality, not the model's own output (§22 metric 10)",
      "The assurance layer's 'no sector observed' critical finding clears (§16)",
    ],
    status: INTEGRATION_STATUS.UNCONNECTED,
  },
  {
    id: "SLOT-VIDEO",
    capability: { en: "Video crowd analytics", mr: "व्हिडिओ गर्दी विश्लेषण" },
    fillsGap: "The Intelligence Router declares a computer-vision paradigm with no engine behind it.",
    section: "§18 · §16",
    approach:
      "Density, flow direction and stagnation estimation from existing ICCC camera feeds. Aggregate metrics returned per camera; frames never leave the source system.",
    contract: {
      direction: "inbound",
      endpoint: "POST /ingest/vision",
      payload: "{ cameraId, ts, densityPerM2, flowBearing, stagnationPct, confidence }",
      cadence: "10-second window",
      classification: "INTERNAL — aggregate metrics only. Face recognition and person re-identification are refused by policy (§17).",
    },
    unlocks: [
      "The cv route stops returning UNAVAILABLE (§18)",
      "Camera entities in the twin gain a live state (§4)",
    ],
    status: INTEGRATION_STATUS.UNCONNECTED,
  },
  {
    id: "SLOT-HEALTH",
    capability: { en: "Syndromic health surveillance", mr: "आरोग्य सर्वेक्षण" },
    fillsGap: "Disease is one of the hazard types with no feed; medical load is derived from heat and density rather than reported cases.",
    section: "§9 · §10",
    approach:
      "Anonymised presentation coding from medical camps and the district health system. Counts by syndrome and sector, never patient records.",
    contract: {
      direction: "inbound",
      endpoint: "POST /ingest/health",
      payload: "{ facilityId, ts, syndromeCode, count }",
      cadence: "15-minute aggregate",
      classification: "INTERNAL — aggregate counts. Patient-level data is out of scope and would be refused.",
    },
    unlocks: [
      "The disease hazard stops reporting UNMONITORED (§9)",
      "Medical stress is measured rather than inferred (§10)",
    ],
    status: INTEGRATION_STATUS.UNCONNECTED,
  },
  {
    id: "SLOT-TRANSPORT",
    capability: { en: "Live transport and arrival feed", mr: "थेट वाहतूक माहिती" },
    fillsGap: "Rail and bus cohorts are fixed demo figures, not a live timetable.",
    section: "§11",
    approach:
      "Scheduled and actual arrival times with passenger estimates, from rail enquiry systems and the state transport corporation.",
    contract: {
      direction: "inbound",
      endpoint: "POST /ingest/arrivals",
      payload: "{ mode, serviceId, scheduledAt, actualAt, estimatedPassengers, terminalId }",
      cadence: "on event",
      classification: "PUBLIC",
    },
    unlocks: [
      "The mobility chain's head demand becomes real (§11)",
      "Transport-surge propagation predicts an actual cohort, not a demo one (§21 mission 3)",
    ],
    status: INTEGRATION_STATUS.UNCONNECTED,
  },
  {
    id: "SLOT-PILGRIM-APP",
    capability: { en: "Pilgrim-facing application", mr: "यात्रेकरू अ‍ॅप" },
    fillsGap: "The platform produces a citizen intelligence payload but has no channel to deliver it.",
    section: "§14",
    approach:
      "A consumer app or WhatsApp-style channel consuming the public projection. This is the one slot that is OUTBOUND — the platform already produces the data.",
    contract: {
      direction: "outbound",
      endpoint: "GET /public/pilgrim-view",
      payload: "Output of buildPilgrimView(): ghat crowd bands, advice, facility locations, emergency numbers. Already redacted through the §17 PUBLIC filter.",
      cadence: "poll, 60 seconds",
      classification: "PUBLIC — the payload is what survives redaction, so a partner cannot receive more than a citizen may see.",
    },
    unlocks: [
      "Crowd-aware guidance actually reaches pilgrims (§14)",
      "Advisory compliance becomes measurable, which the intervention simulator currently has to assume (§6)",
    ],
    status: INTEGRATION_STATUS.SPEC_READY,
  },
  {
    id: "SLOT-DISPATCH",
    capability: { en: "Emergency dispatch integration", mr: "आपत्कालीन रवानगी" },
    fillsGap: "No unit has ever been dispatched by this platform. Response ETAs are computed from invented positions.",
    section: "§12 · §22",
    approach:
      "Two-way integration with 108 ambulance dispatch and police control room: unit positions and status inbound, authorised tasking outbound.",
    contract: {
      direction: "bidirectional",
      endpoint: "POST /ingest/units · POST /dispatch/task",
      payload: "{ unitId, coord, status, capability } inbound · { taskId, unitId, incidentId, authorisedBy } outbound",
      cadence: "30-second position, on-event tasking",
      classification: "RESTRICTED — unit positions are restricted because publishing them reveals where response is thin.",
    },
    unlocks: [
      "Field response time and resolution time become measurable (§22 metrics 8 and 9)",
      "Responder selection runs against real availability (§12)",
    ],
    status: INTEGRATION_STATUS.UNCONNECTED,
  },
  {
    id: "SLOT-SOP",
    capability: { en: "Approved SOP library", mr: "मंजूर कार्यपद्धती" },
    fillsGap: "The document corpus holds seven ILLUSTRATIVE procedures. None has been issued by any authority.",
    section: "§13 · §18",
    approach:
      "The district administration's issued standing orders, loaded as the retrieval corpus so the copilot cites approved procedure instead of a demonstration paraphrase.",
    contract: {
      direction: "inbound",
      endpoint: "POST /ingest/sop",
      payload: "{ docId, title, issuedBy, issuedAt, version, body, supersedes }",
      cadence: "on publication",
      classification: "INTERNAL",
    },
    unlocks: [
      "SOP answers can carry approved: true for the first time (§13)",
      "The illustrative-only warning can be removed from retrieved procedure",
    ],
    status: INTEGRATION_STATUS.UNCONNECTED,
  },
  {
    id: "SLOT-AUDIT",
    capability: { en: "Append-only evidence store", mr: "अपरिवर्तनीय पुरावा संग्रह" },
    fillsGap: "The event log lives in browser storage, which anyone can clear. It is not an audit record and says so.",
    section: "§15 · §17",
    approach:
      "A server-side append-only store with hash chaining verified outside the client, so a decision record survives the browser and cannot be edited after the fact.",
    contract: {
      direction: "outbound",
      endpoint: "POST /evidence/append",
      payload: "Output of buildEvidenceEnvelope(): all 16 decision-provenance fields plus the chain digest.",
      cadence: "on every state transition",
      classification: "RESTRICTED — contains the authorising officer's identity.",
    },
    unlocks: [
      "Immutable audit moves from 'declared architecture' to 'enforced' (§17)",
      "Evidence completeness becomes a durable measure rather than a session one (§22 metric 13)",
    ],
    status: INTEGRATION_STATUS.UNCONNECTED,
  },
];

/* Principles any integration must satisfy. These are not aspirational —
 * each maps to a control already enforced in governance/privacy.js, so a
 * partner integration cannot be granted more than the model allows. */
export const INTEGRATION_PRINCIPLES = [
  {
    principle: { en: "Aggregate, never identity", mr: "एकत्रित, ओळख नाही" },
    detail: "No integration may supply or request biometric identifiers, device identifiers or per-person tracking. These five categories throw on access rather than returning empty.",
    enforcedBy: "governance/privacy.js · PROHIBITED_CATEGORIES",
  },
  {
    principle: { en: "Partners get the public projection", mr: "भागीदारांना सार्वजनिक माहिती" },
    detail: "An outbound integration receives what survives redaction for its clearance. The Kumbhathon partner profile holds PUBLIC clearance, so it can receive no more than a citizen may see.",
    enforcedBy: "governance/privacy.js · redactFor()",
  },
  {
    principle: { en: "Purpose is declared and checked", mr: "उद्देश घोषित व तपासलेला" },
    detail: "Clearance alone is not authorisation. An integration declares a purpose and is refused data outside it even where its clearance would otherwise allow access.",
    enforcedBy: "governance/privacy.js · canAccess()",
  },
  {
    principle: { en: "Inbound data carries provenance", mr: "आलेल्या माहितीसह स्रोत" },
    detail: "A feed must declare its source kind and confidence. Data arriving without provenance is marked UNVERIFIED and cannot raise an assessment above that level.",
    enforcedBy: "geo/data/provenance.js",
  },
  {
    principle: { en: "No integration may authorise action", mr: "कोणतीही भागीदारी कार्यवाही मंजूर करू शकत नाही" },
    detail: "A partner system may supply data and receive advisories. It may not approve an intervention or dispatch a unit — those transitions require a named government officer.",
    enforcedBy: "spine/incidentMachine.js",
  },
];

export function integrationSummary() {
  const byStatus = (s) => INTEGRATION_SLOTS.filter((x) => x.status === s).length;
  return {
    total: INTEGRATION_SLOTS.length,
    unconnected: byStatus(INTEGRATION_STATUS.UNCONNECTED),
    specReady: byStatus(INTEGRATION_STATUS.SPEC_READY),
    pilot: byStatus(INTEGRATION_STATUS.PILOT),
    live: byStatus(INTEGRATION_STATUS.LIVE),
    inbound: INTEGRATION_SLOTS.filter((s) => s.contract.direction === "inbound").length,
    outbound: INTEGRATION_SLOTS.filter((s) => s.contract.direction !== "inbound").length,
    statement:
      `${INTEGRATION_SLOTS.length} capability slots are defined, every one of them derived from a gap this platform reports about itself. `
      + `${byStatus(INTEGRATION_STATUS.LIVE)} are live. No partnership or data-sharing agreement exists with any organisation.`,
  };
}
